import { FunctionComponent, useCallback, useState, ChangeEvent } from 'react'
import { MdCheck } from 'react-icons/md'
import { IoMdEyeOff, IoMdEye } from 'react-icons/io'
import { useHistory, useParams } from 'react-router-dom'
import * as Yup from 'yup'

import iconAmpulheta from 'shared/assets/icons/icon-ampulheta.png'
import iconKey from 'shared/assets/icons/icon-key.svg'
import Form, { IProps as FormProps } from 'shared/components/Form'
import Input from 'shared/components/Input'
import ValidatePassword from 'shared/components/ValidatePassword'
import { colors } from 'shared/styles/colors'

import { Feedback } from 'shared/components/Feedback'
import Text from 'shared/components/Text'
import { registerEvent } from 'shared/services/TrackingGAService'
import dimensions from 'shared/styles/dimensions'

import {
  ContainerForm,
  Icon,
  ContainerInput,
  ButtonShowPassword,
  HelperMessage,
  ContainerAtencao,
  Container,
  StyledPage,
} from './styles'
import { useNewPassword } from 'modules/user/hooks/newPassword'
import { NewPasswordProps } from './NewPasswordComponent.types'
import { steps } from '../..'
import Button from 'shared/components/Button'

const NewPasswordScreen: FunctionComponent<NewPasswordProps> = ({
  changeGestorImpl,
  setSelectedStep,
}) => {
  const { hash } = useParams<{ hash: string | undefined }>()
  const history = useHistory()
  const {
    newUserPassword,
    setNewPassword,
    isPasswordValid,
    confirmPassword,
    setConfirmPassword,
  } = useNewPassword()
  const [signUpOk, setSignUpOk] = useState(false)
  const [loading, setLoading] = useState(false)
  const [errorMessage, setErrorMessage] = useState('')
  const [showNewPassword, setShowNewPassword] = useState(false)
  const [showConfirmNewPassword, setShowConfirmNewPassword] = useState(false)

  const clearInputsPassword = () => {
    setNewPassword('')
    setConfirmPassword('')
  }

  const changeGestor = useCallback(
    async (values: FormProps['values']) => {
      try {
        await changeGestorImpl.execute({
          email: localStorage.getItem('@TMov:ChangeGestorEmail'),
          telefone: localStorage.getItem('@TMov:ChangeGestorPhone'),
          doc_imagem_frente: localStorage.getItem(
            '@TMov:ChangeGestorPhotoBase64',
          ),
          doc_imagem_verso: localStorage.getItem(
            '@TMov:ChangeGestorPhotoBase64',
          ),
          doc_token_sdk: '',
          senha: values.newPassword,
          hash,
        })

        setLoading(false)
        setSignUpOk(true)
      } catch (e: unknown) {
        setLoading(false)
        if (e instanceof Error) {
          setErrorMessage(e.message)
        } else {
          setErrorMessage('Erro ao tentar criar frotista')
        }
      }
    },
    [changeGestorImpl],
  )

  const handleSubmit = useCallback(
    async (values: FormProps['values']) => {
      registerEvent({
        categoria: 'Cadastro',
        acao: 'sign_up',
        local: 'Botao na tela de criação da senha',
      })

      setLoading(true)
      setErrorMessage('')

      try {
        const schema = Yup.object().shape({
          newPassword: Yup.string().nullable(),
          confirmPassword: Yup.string().oneOf(
            [Yup.ref('newPassword'), null],
            'As senhas não são iguais. Tente novamente.',
          ),
        })

        await schema.validate(values, {
          abortEarly: false,
        })

        changeGestor(values)
        return
      } catch (err: unknown) {
        setLoading(false)
        if (err instanceof Yup.ValidationError) {
          setErrorMessage(err.message)
        } else if (err instanceof Error) {
          setErrorMessage(err.message)
        } else {
          setErrorMessage('Erro tentar setar a nova senha')
        }
      }
    },
    [changeGestor],
  )

  if (signUpOk) {
    return (
      <div style={{ paddingTop: 50 }}>
        <Feedback
          icon={iconAmpulheta}
          colorIcon={colors.white}
          title="Solicitação de cadastro efetuada com sucesso!"
          subTitle="Estamos analisando os dados e em breve te retornaremos."
          body={
            <ContainerAtencao>
              <Text
                color={colors.orange}
                size={dimensions.fontSize.largex2}
                weight={'600'}
              >
                ATENÇÃO:
              </Text>
              <Text
                color={colors.white}
                size={dimensions.fontSize.large}
                weight={'normal'}
              >
                Fique atento ao seu email. Caso não tenho recebido a <br />
                confirmação de cadastro, verifique sua caixa de spam.
              </Text>
            </ContainerAtencao>
          }
          onClickButton={() => {
            history.push('/')
          }}
        />
      </div>
    )
  }

  return (
    <StyledPage
      icon={iconKey}
      title="Criar senha"
      goBackTo={() => {
        setSelectedStep(steps.Photo)
        clearInputsPassword()
      }}
    >
      <Container>
        <Form
          onSubmit={handleSubmit}
          initialValues={{
            newPassword: '',
            confirmPassword: '',
          }}
          validationSchema={() => ({
            newPassword: Yup.string().nullable(),
            confirmPassword: Yup.string().nullable(),
          })}
        >
          {formControl => (
            <ContainerForm>
              <ContainerInput>
                <Input
                  placeholder="Criar senha"
                  name="newPassword"
                  type={showNewPassword ? 'text' : 'password'}
                  formControl={formControl}
                  errorMessage={''}
                  onChange={(e: ChangeEvent<HTMLInputElement>) => {
                    setNewPassword(e.target.value)
                  }}
                >
                  <ButtonShowPassword
                    onClick={() => {
                      setShowNewPassword(!showNewPassword)
                    }}
                  >
                    <Icon>
                      {!showNewPassword ? <IoMdEye /> : <IoMdEyeOff />}
                    </Icon>
                  </ButtonShowPassword>
                </Input>
              </ContainerInput>
              <ContainerInput>
                <Input
                  placeholder="Confirme sua senha"
                  name="confirmPassword"
                  type={showConfirmNewPassword ? 'text' : 'password'}
                  formControl={formControl}
                  errorMessage={''}
                  onChange={(e: ChangeEvent<HTMLInputElement>) => {
                    setConfirmPassword(e.target.value)
                  }}
                >
                  <ButtonShowPassword
                    onClick={() => {
                      setShowConfirmNewPassword(!showConfirmNewPassword)
                    }}
                  >
                    <Icon>
                      {!showConfirmNewPassword ? <IoMdEye /> : <IoMdEyeOff />}
                    </Icon>
                  </ButtonShowPassword>
                </Input>
              </ContainerInput>

              {errorMessage && <HelperMessage>{errorMessage}</HelperMessage>}
              <Container>
                <ValidatePassword />
              </Container>

              <Button
                color={
                  newUserPassword && confirmPassword && isPasswordValid
                    ? colors.green
                    : colors.grayLight
                }
                type="submit"
                onClick={formControl.handleSubmit}
                loading={loading}
                disabled={
                  !(newUserPassword && confirmPassword && isPasswordValid) ||
                  loading
                }
              >
                <MdCheck /> Salvar
              </Button>
            </ContainerForm>
          )}
        </Form>
      </Container>
    </StyledPage>
  )
}

export default NewPasswordScreen
